import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useEstateContext } from '@/lib/EstateContext';
import { useSubscription } from '@/hooks/useSubscription';
import { estatesService } from '@/services';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Zap, Building2, ArrowRight, Loader2, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

const PRICING_TIERS = [
  {
    id: 'small',
    name: 'Small Estate',
    price: 'R599',
    discountedPrice: 'R539',
    period: '/month',
    description: 'For estates with up to 50 homes',
    features: [
      'Up to 50 homes',
      'Full FixMyHood platform included',
      'Internal issue tracking',
      'Contractor management',
      'Monthly reports',
      'Export CSV data',
      'Unlimited reports',
      'Email support'
    ],
    icon: Building2,
    color: 'emerald'
  },
  {
    id: 'medium',
    name: 'Medium Estate',
    price: 'R1,149',
    discountedPrice: 'R1,034',
    period: '/month',
    description: 'For estates with 51–200 homes',
    features: [
      '51–200 homes',
      'Full FixMyHood platform included',
      'Internal issue tracking',
      'Contractor management',
      'Monthly reports',
      'Export CSV data',
      'Unlimited reports',
      'Email support'
    ],
    icon: Zap,
    color: 'blue',
    popular: true
  },
  {
    id: 'large',
    name: 'Large Estate',
    price: 'R1,999',
    discountedPrice: 'R1,799',
    period: '/month',
    description: 'For estates with 201+ homes',
    features: [
      '201+ homes',
      'Full FixMyHood platform included',
      'Internal issue tracking',
      'Contractor management',
      'Monthly reports',
      'Export CSV data',
      'Unlimited reports',
      'Email support'
    ],
    icon: Crown,
    color: 'purple'
  }
];

export default function SubscriptionTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeEstateId } = useEstateContext();
  const { subscription, isLoading } = useSubscription();
  const [upgrading, setUpgrading] = React.useState(false);
  const queryClient = useQueryClient();

  // Fetch estate count to determine multi-estate discount eligibility
  const { data: myEstates = [] } = useQuery({
    queryKey: ['my_estates_for_discount', user?.id],
    queryFn: () => estatesService.getMyEstates(user.id),
    enabled: !!user?.id && user?.user_role === 'estate_manager',
    staleTime: 1000 * 60,
  });
  const estateCount = myEstates.length;
  const hasDiscount = estateCount >= 2;
  const DISCOUNT = 0.10; // 10% flat multi-estate discount

  const estateId = activeEstateId || user?.estate_id;
  const currentTier = subscription?.subscription_tier || 'trial';
  const isActive = subscription?.subscription_status === 'active';

  const handleUpgrade = async (tier) => {
    if (!estateId) {
      toast.error('No estate found');
      return;
    }

    setUpgrading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/paystack-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          tier,
          estateId,
          applyDiscount: hasDiscount,
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      window.location.href = data.authorization_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again or contact support.');
      setUpgrading(false);
    }
  };

  const handleManageSubscription = () => {
    // Navigate to dedicated subscription management page
    navigate(createPageUrl('ManageSubscription'));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Multi-estate discount banner */}
      {hasDiscount && (
        <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <Tag className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-900 dark:text-green-200">
                10% multi-estate discount applied
              </p>
              <p className="text-xs text-green-700 dark:text-green-400">
                You manage {estateCount} estates — all plans are discounted across your account.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Current Plan */}
      {isActive && (
        <Card className="p-6 bg-white dark:bg-slate-800 border-2 border-[#1883FF] shadow-lg">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                Current Plan
              </h3>
              <div className="flex items-center gap-2">
                <Badge className="bg-[#1883FF] text-white capitalize">
                  {currentTier} Estate
                </Badge>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Active subscription
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManageSubscription}
              className="border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Manage Subscription
            </Button>
          </div>
        </Card>
      )}

      {/* Trial Status */}
      {!isActive && subscription?.subscription_status === 'trial' && (
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                Free Trial
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {subscription.daysRemaining > 0
                  ? `${subscription.daysRemaining} days remaining`
                  : 'Trial expired'}
              </p>
            </div>
            <Badge variant="outline" className="border-blue-600 text-blue-600">
              Trial
            </Badge>
          </div>
        </Card>
      )}

      {/* Pricing Plans */}
      <div>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              Choose Your Plan
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
              Plans are priced based on the size of your estate. All plans include full access to all features.
            </p>
            {isActive && (
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Upgrades and downgrades are prorated automatically. Changes take effect immediately.
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRICING_TIERS.map((tier) => {
            const Icon = tier.icon;
            const isCurrent = currentTier === tier.id && isActive;
            const colorClasses = {
              emerald: 'from-emerald-500 to-teal-600',
              blue: 'from-blue-500 to-indigo-600',
              purple: 'from-purple-500 to-pink-600'
            };

            return (
              <motion.div
                key={tier.id}
                whileHover={{ y: -4 }}
                className="relative"
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-[#1883FF] text-white shadow-lg">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <Card
                  className={`p-6 h-full flex flex-col ${
                    tier.popular
                      ? 'border-2 border-[#1883FF] shadow-lg'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  {/* Header */}
                  <div className="mb-6">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[tier.color]} flex items-center justify-center mb-4`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                      {tier.name}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {tier.description}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    {hasDiscount ? (
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-bold text-slate-900 dark:text-white">
                            {tier.discountedPrice}
                          </span>
                          <span className="text-slate-600 dark:text-slate-400">{tier.period}</span>
                        </div>
                        <p className="text-xs text-slate-400 line-through mt-0.5">{tier.price}/month</p>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-slate-900 dark:text-white">
                          {tier.price}
                        </span>
                        <span className="text-slate-600 dark:text-slate-400">
                          {tier.period}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-grow">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-600 dark:text-slate-300">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button */}
                  {isCurrent ? (
                    <Badge className="w-full justify-center py-2 bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300">
                      Current Plan
                    </Badge>
                  ) : (
                    <Button
                      onClick={() => handleUpgrade(tier.id)}
                      disabled={upgrading}
                      className={`w-full rounded-[24px] md:rounded-[40px] ${
                        tier.popular
                          ? 'bg-[#1883FF] hover:bg-[#0066CC]'
                          : 'bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900'
                      }`}
                    >
                      {upgrading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          {isActive && currentTier !== tier.id
                            ? currentTier === 'small' && (tier.id === 'medium' || tier.id === 'large')
                              ? 'Upgrade Plan'
                              : currentTier === 'medium' && tier.id === 'large'
                              ? 'Upgrade Plan'
                              : 'Switch Plan'
                            : 'Get Started'}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      
    </div>
  );
}
